import React from 'react';
import { Stethoscope, Dna, Brain, Star, FileText, Activity, Lock } from 'lucide-react';

interface PhoneMockupProps {
  imageSrc?: string;
  children?: React.ReactNode;
  className?: string;
  frameColor?: 'dark' | 'light';
}

export const PhoneMockup: React.FC<PhoneMockupProps> = ({ 
  imageSrc, 
  children, 
  className = '',
  frameColor = 'dark'
}) => {
  const borderColor = frameColor === 'dark' ? 'border-gray-900 bg-gray-900' : 'border-white bg-white';
  const innerBorder = frameColor === 'dark' ? 'bg-gray-800' : 'bg-gray-200';

  return (
    <div className={`relative mx-auto ${borderColor} border-[10px] rounded-[2.5rem] h-[500px] w-[260px] md:h-[600px] md:w-[300px] shadow-2xl ${className}`}>
      <div className={`w-[100px] h-[18px] ${frameColor === 'dark' ? 'bg-gray-900' : 'bg-white'} top-0 rounded-b-[1rem] left-1/2 -translate-x-1/2 absolute z-20`}></div>
      <div className={`h-[32px] w-[3px] ${innerBorder} absolute -start-[14px] top-[72px] rounded-s-lg`}></div>
      <div className={`h-[46px] w-[3px] ${innerBorder} absolute -start-[14px] top-[124px] rounded-s-lg`}></div>
      <div className={`h-[46px] w-[3px] ${innerBorder} absolute -end-[14px] top-[142px] rounded-e-lg`}></div>
      <div className="rounded-[2rem] overflow-hidden w-full h-full bg-white relative z-10 flex flex-col">
        {imageSrc ? (
          <img src={imageSrc} alt="App Screen" className="w-full h-full object-cover" />
        ) : (
          children
        )}
      </div>
    </div>
  );
};

// Internal component to simulate the App UI for the mockup without needing external screenshots
export const AppUiPlaceholder: React.FC<{ type?: 'home' | 'booking' | 'profile' | 'records' }> = ({ type = 'home' }) => {
  return (
    <div className="w-full h-full bg-gray-50 flex flex-col font-sans">
      {/* App Header */}
      <div className="bg-white p-4 pt-8 shadow-sm flex justify-between items-center z-10">
        <div className="flex items-center gap-2">
           <div className="w-8 h-8 bg-primary rounded-full overflow-hidden">
             <img src="https://picsum.photos/100/100?random=10" alt="User" className="w-full h-full object-cover" />
           </div>
           <div>
             <p className="text-xs text-gray-500">Good Morning,</p>
             <p className="text-sm font-bold text-secondary">Michael</p>
           </div>
        </div>
        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
        </div>
      </div>

      {/* App Content */}
      <div className="p-4 flex-1 overflow-hidden relative overflow-y-auto hide-scrollbar">
        {type === 'home' && (
            <>
                <div className="bg-primary rounded-2xl p-4 text-white mb-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
                    <h3 className="font-bold text-lg mb-1">Book your visit</h3>
                    <p className="text-xs text-white/80 mb-3">Specialists available today</p>
                    <button className="bg-white text-primary text-xs px-3 py-1.5 rounded-full font-medium">Book Now</button>
                </div>

                <h4 className="font-bold text-secondary text-sm mb-3">Categories</h4>
                <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
                    {['General', 'Urology', 'Therapy'].map((cat, i) => (
                        <div key={i} className="flex-shrink-0 flex flex-col items-center gap-1">
                            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-xl text-primary">
                                {i === 0 ? <Stethoscope size={20} /> : i === 1 ? <Dna size={20} /> : <Brain size={20} />}
                            </div>
                            <span className="text-[10px] text-gray-500">{cat}</span>
                        </div>
                    ))}
                </div>

                 <h4 className="font-bold text-secondary text-sm mb-3 mt-4">Top Doctors</h4>
                 <div className="space-y-3">
                    {[1, 2].map((i) => (
                        <div key={i} className="bg-white p-3 rounded-xl shadow-sm flex items-center gap-3">
                            <img src={`https://picsum.photos/100/100?random=${20+i}`} className="w-10 h-10 rounded-full object-cover" />
                            <div className="flex-1">
                                <p className="text-xs font-bold text-secondary">Dr. James Wilson</p>
                                <p className="text-[10px] text-gray-400 flex items-center gap-1">
                                    Urologist • 4.9 <Star size={10} className="fill-yellow-400 text-yellow-400" />
                                </p>
                            </div>
                        </div>
                    ))}
                 </div>
            </>
        )}

        {type === 'records' && (
           <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                 <h3 className="font-bold text-secondary">Medical Records</h3>
                 <span className="text-xs text-primary font-bold">See All</span>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6">
                 <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 text-primary rounded-lg">
                       <Lock size={18} />
                    </div>
                    <div>
                       <h4 className="font-bold text-sm text-secondary">Encrypted Storage</h4>
                       <p className="text-[10px] text-gray-500 leading-tight mt-1">Your data is secured with AES-256 encryption.</p>
                    </div>
                 </div>
              </div>

              {[
                 { title: "Blood Test Results", date: "Today, 10:00 AM", icon: Activity, color: "text-red-500 bg-red-50" },
                 { title: "Cardiology Report", date: "Yesterday", icon: FileText, color: "text-blue-500 bg-blue-50" },
                 { title: "General Checkup", date: "Sept 12, 2025", icon: Stethoscope, color: "text-green-500 bg-green-50" }
              ].map((item, i) => (
                 <div key={i} className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${item.color} flex items-center justify-center`}>
                       <item.icon size={18} />
                    </div>
                    <div className="flex-1">
                       <p className="text-sm font-bold text-secondary">{item.title}</p>
                       <p className="text-[10px] text-gray-400">{item.date}</p>
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center text-gray-300">
                       <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </div>
                 </div>
              ))}
           </div>
        )}
      </div>

      {/* Bottom Nav */}
      <div className="bg-white p-4 pb-6 shadow-[0_-5px_15px_rgba(0,0,0,0.05)] flex justify-between items-center text-gray-300 border-t border-gray-50">
         <div className={`${type === 'home' ? 'text-primary' : 'text-gray-400'}`}><svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/></svg></div>
         <div className={`${type === 'records' ? 'text-primary' : 'text-gray-400'}`}><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg></div>
         <div className="text-gray-400"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg></div>
      </div>
    </div>
  );
};